import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Input, Label } from 'reactstrap';
import { v4 as uuid } from 'uuid';

import { calculateWeight } from '@techchange/dnd';
import { messages } from '../../../../../../_shared/i18n/messages';

import { CONTINUATION_TYPES } from '../../../../../constants';
import { useFormEditorContext } from '../../../contexts/FormEditorContext';
import {
	FIELD_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
} from '../../../hooks/useFormEditorData';
import { formFieldOptionDataShape } from '../../../utils/propTypes';

import FormEditorFieldOptionLikert from './FormEditorFieldOptionLikert';

import './FormEditorFieldOptionsLikert.scss';

const LIKERT_SCALE_RANGE_MIN = [0, 1];
const LIKERT_SCALE_RANGE_MAX = [2, 3, 4, 5, 6, 7, 8, 9, 10];

const propTypes = {

	/**
	 * The id of the field.
	 */
	fieldId: PropTypes.string.isRequired,

	/**
	 * The list of form field options.
	 */
	options: PropTypes.arrayOf(formFieldOptionDataShape).isRequired,

};

const FormEditorFieldOptionsLikert = (props) => {

	const { fieldId, options } = props;

	const {
		isEditingDefaultLanguage,
		defaultLanguage,
		actions,
	} = useFormEditorContext();

	// Sort the likert options in ascending order based on the value
	const sortedOptions = useMemo(() => {

		return options.sort((option1, option2) =>
			parseInt(option1[OPTION_EDITOR_FIELDS.VALUE], 10) -
			parseInt(option2[OPTION_EDITOR_FIELDS.VALUE], 10)
		);

	}, [options]);

	const onMinChange = (event) => {

		const oldValue = parseInt(sortedOptions[0][OPTION_EDITOR_FIELDS.VALUE], 10);
		const newValue = parseInt(event.target.value, 10);

		const updatedOptions = [...sortedOptions];

		// Check if the minimum value didn't change
		if (newValue === oldValue) {
			return;
		}

		if (newValue === 0 && oldValue === 1) {

			const next = sortedOptions[0][OPTION_EDITOR_FIELDS.WEIGHT];

			updatedOptions.unshift({
				[OPTION_EDITOR_FIELDS.ID]: uuid(),
				[OPTION_EDITOR_FIELDS.VALUE]: newValue.toString(),
				[OPTION_EDITOR_FIELDS.CONTINUATION_TYPE]: CONTINUATION_TYPES.DEFAULT,
				[OPTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID]: null,
				[OPTION_EDITOR_FIELDS.WEIGHT]: calculateWeight(null, next),
				[OPTION_EDITOR_FIELDS.FORM_FIELD_ID]: fieldId,
				[OPTION_EDITOR_FIELDS.CONTENTS]: [{
					language: defaultLanguage,
					[OPTION_EDITOR_FIELDS.LABEL]: newValue.toString(),
				}],
				[OPTION_EDITOR_FIELDS.DELETED_CONTENTS]: [],
			});

		} else if (newValue === 1 && oldValue === 0) {

			updatedOptions.shift();

		}

		actions.fields.update(fieldId, FIELD_EDITOR_FIELDS.OPTIONS, updatedOptions);

	};

	const onMaxChange = (event) => {

		const newValue = parseInt(event.target.value, 10);
		const minValue = parseInt(sortedOptions[0][OPTION_EDITOR_FIELDS.VALUE], 10);

		const updatedOptions = [];

		for (let i = minValue; i <= newValue; i++) {

			const existingOption = sortedOptions.find((option) =>
				parseInt(option[OPTION_EDITOR_FIELDS.VALUE], 10) === i
			);

			if (existingOption) {

				updatedOptions.push(existingOption);

			} else {

				updatedOptions.push({
					[OPTION_EDITOR_FIELDS.ID]: uuid(),
					[OPTION_EDITOR_FIELDS.VALUE]: i.toString(),
					[OPTION_EDITOR_FIELDS.CONTINUATION_TYPE]: CONTINUATION_TYPES.DEFAULT,
					[OPTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID]: null,
					[OPTION_EDITOR_FIELDS.WEIGHT]: i * 10,
					[OPTION_EDITOR_FIELDS.FORM_FIELD_ID]: fieldId,
					[OPTION_EDITOR_FIELDS.CONTENTS]: [{
						language: defaultLanguage,
						[OPTION_EDITOR_FIELDS.LABEL]: i.toString(),
					}],
					[OPTION_EDITOR_FIELDS.DELETED_CONTENTS]: [],
				});

			}

		}

		actions.fields.update(fieldId, FIELD_EDITOR_FIELDS.OPTIONS, updatedOptions);

	};

	const currentMin = parseInt(
		sortedOptions[0][OPTION_EDITOR_FIELDS.VALUE],
		10,
	);

	const currentMax = parseInt(
		sortedOptions[sortedOptions.length - 1][OPTION_EDITOR_FIELDS.VALUE],
		10,
	);

	return (
		<div className="tc-form-editor-field-options">
			<div className="tc-form-editor-field-options-likert-scale-range">
				<Label
					for={`tc-form-editor-field-option-${fieldId}-minimum-value`}
					hidden>
					<FormattedMessage
						{...messages.TC_COMPONENTS_FORM_EDITOR_LIKERT_SCALE_CHOOSE_MIN_VALUE_LABEL} />
				</Label>
				<Input
					name={`tc-form-editor-field-option-${fieldId}-minimum-value`}
					type="select"
					value={currentMin}
					disabled={!isEditingDefaultLanguage}
					onChange={onMinChange}>
					{LIKERT_SCALE_RANGE_MIN.map((val) => <option key={val}>{val}</option>)}
				</Input>
				{' '}
				<span>
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_RANGE_GOES_TO} />
				</span>
				{' '}
				<Label
					for={`tc-form-editor-field-option-${fieldId}-maximum-value`}
					hidden>
					<FormattedMessage
						{...messages.TC_COMPONENTS_FORM_EDITOR_LIKERT_SCALE_CHOOSE_MAX_VALUE_LABEL} />
				</Label>
				<Input
					name={`tc-form-editor-field-option-${fieldId}-maximum-value`}
					type="select"
					value={currentMax}
					disabled={!isEditingDefaultLanguage}
					onChange={onMaxChange}>
					{LIKERT_SCALE_RANGE_MAX.map((val) => <option key={val}>{val}</option>)}
				</Input>
			</div>
			{sortedOptions.map((option) => (
				<FormEditorFieldOptionLikert
					key={option.id}
					fieldId={fieldId}
					option={option} />
			))}
		</div>
	);

};

FormEditorFieldOptionsLikert.propTypes = propTypes;

export default FormEditorFieldOptionsLikert;
